import type {
  EntityTypeEnum,
  FeatureFlagEnum,
  SubFormTypeEnum,
  VisibilityRule,
} from 'src/api/ProactApiClient';
import type {IFormFieldDropDownOptions, ISelectOption} from './Form';

// Form builder settings types. These live here (rather than in the redux
// slice that consumes them) so that `types/` never needs to import from
// `redux/` — the slice imports the types from here instead.
export type IReferenceImage = Record<string, unknown>;
export interface IFormSettingsMetadataCache {
  referenceImage?: IReferenceImage[];
}
export interface IFormSettingsMetadata {
  cache?: IFormSettingsMetadataCache;
  anonymousSubmission: boolean;
  description?: string;
  enableFormIdentifierField: boolean;
}
export interface IFormSettings {
  identifier: string;
  active: boolean;
  name: string;
  title: string;
  subformOnly: boolean;
  extensionOnly: boolean;
  triggerAnalyticSafetyEvent: boolean;
  failable: boolean;
  accessAllowSupervisors: boolean;
  accessAllowEditAfterPublish: boolean;
  accessAllowUserGroups: number[];
  metadata: IFormSettingsMetadata;
  version?: string;
  mode: 'create' | 'edit';
  lockIdentifier: boolean;
  projectId?: number | null;
  categoryId?: number | null;
  sensitiveData: boolean;
}

// Character limit constants for text-based form fields
export const TEXT_MAX_LENGTH_DEFAULT = 1500;
export const TEXT_MAX_LENGTH_ABSOLUTE = 25000;
export const RICHTEXT_MAX_LENGTH_DEFAULT = 50000;
export const RICHTEXT_MAX_LENGTH_ABSOLUTE = 100000;

export type FormFieldTypeV2 =
  | 'text'
  | 'textarea'
  | 'select'
  | 'autocomplete'
  | 'date'
  | 'switch'
  | 'number'
  | 'richtext'
  | 'file'
  | 'asset'
  | 'person'
  | 'person-select-multiple'
  | 'scale'
  | 'signature'
  | 'drawing-pad'
  | 'phone'
  | 'select-multiple'
  | 'paragraph'
  | 'heading'
  | 'divider'
  | 'subform'
  | 'video-select'
  | 'project'
  | 'measurement'
  | 'tags';

export const FormFieldTypeDisplayNameV2: Record<FormFieldTypeV2, string> = {
  text: 'Text',
  textarea: 'Text Area',
  select: 'Select',
  autocomplete: 'Autocomplete',
  date: 'Date',
  switch: 'Yes/No',
  number: 'Number',
  richtext: 'Rich Text',
  file: 'File Upload',
  asset: 'Asset',
  person: 'Person',
  'person-select-multiple': 'Multiple Person Select',
  scale: 'Scale',
  signature: 'Signature',
  'drawing-pad': 'Drawing Pad',
  phone: 'Phone Number',
  'select-multiple': 'Multiple Select',
  paragraph: 'Paragraph',
  heading: 'Heading',
  divider: 'Divider',
  subform: 'Subform',
  'video-select': 'Video Select',
  project: 'Project',
  measurement: 'Measurement',
  tags: 'Tags',
};

export const FormBuilderFieldTypeV2: FormFieldTypeV2[] = [
  'text',
  'textarea',
  'select',
  'select-multiple',
  'date',
  'switch',
  'number',
  'richtext',
  'file',
  'asset',
  'person',
  'scale',
  'signature',
  'drawing-pad',
  'phone',
  'paragraph',
  'heading',
  'divider',
  'subform',
];

export const FormBuilderFieldTypeOptionsV2: ISelectOption[] = (
  FormBuilderFieldTypeV2 as FormFieldTypeV2[]
)
  .map(type => ({
    id: type,
    label: FormFieldTypeDisplayNameV2[type],
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const NotificationRuleFieldTypeV2: FormFieldTypeV2[] = [
  'text',
  'textarea',
  'select',
  'autocomplete',
  'date',
  'switch',
  'number',
  'person',
  'person-select-multiple',
  'scale',
  'phone',
  'select-multiple',
  'asset',
  'project',
];

export const VisibilutyRuleFieldTypeV2: FormFieldTypeV2[] = [
  'text',
  'textarea',
  'select',
  'select-multiple',
  'autocomplete',
  'date',
  'switch',
  'number',
  'asset',
  'person',
  'scale',
  'phone',
];

export const FieldConfigurationV2: Record<
  FormFieldTypeV2,
  {
    showDescription: boolean;
    showDefaultValue: boolean;
    showRequired: boolean;
    showMultiline: boolean;
    showValidation: boolean;
    showMaxLength: boolean;
  }
> = {
  text: {
    showDescription: true,
    showDefaultValue: true,
    showRequired: true,
    showMultiline: true,
    showValidation: true,
    showMaxLength: true,
  },
  textarea: {
    showDescription: true,
    showDefaultValue: true,
    showRequired: true,
    showMultiline: false,
    showValidation: true,
    showMaxLength: true,
  },
  select: {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  'select-multiple': {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  autocomplete: {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  date: {
    showDescription: true,
    showDefaultValue: true,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  switch: {
    showDescription: true,
    showDefaultValue: true,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  number: {
    showDescription: true,
    showDefaultValue: true,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  richtext: {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: true,
  },
  file: {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  asset: {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  person: {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  'person-select-multiple': {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  scale: {
    showDescription: true,
    showDefaultValue: true,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  signature: {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  'drawing-pad': {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  phone: {
    showDescription: true,
    showDefaultValue: true,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  heading: {
    showDescription: false,
    showDefaultValue: false,
    showRequired: false,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  paragraph: {
    showDescription: false,
    showDefaultValue: false,
    showRequired: false,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  divider: {
    showDescription: false,
    showDefaultValue: false,
    showRequired: false,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  subform: {
    showDescription: false,
    showDefaultValue: false,
    showRequired: false,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  'video-select': {
    showDescription: true,
    showDefaultValue: false,
    showRequired: false,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  project: {
    showDescription: true,
    showDefaultValue: false,
    showRequired: false,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  measurement: {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
  tags: {
    showDescription: true,
    showDefaultValue: false,
    showRequired: true,
    showMultiline: false,
    showValidation: false,
    showMaxLength: false,
  },
};

export type FieldSizeV2 = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const FieldSizeOptionsV2: ISelectOption[] = [
  {id: 'sm', label: 'Small'},
  {id: 'md', label: 'Medium'},
  {id: 'xl', label: 'Large'},
];

export type ValidationTypeV2 =
  | 'name'
  | 'email'
  | 'address'
  | 'url'
  | 'percent'
  | 'currency'
  | 'decimal'
  | 'time'
  | 'safe';

export const ValidationTypeDisplayNameV2: Record<ValidationTypeV2, string> = {
  name: 'Name',
  email: 'Email Address',
  address: 'Street Address',
  url: 'Website URL',
  percent: 'Percentage',
  currency: 'Currency',
  decimal: 'Decimal Number',
  time: 'Time',
  safe: 'Standard Letters, Numbers, and Basic Symbols',
};

export const ValidationTypeOptionsV2 = Object.entries(
  ValidationTypeDisplayNameV2
).map(([id, label]) => ({
  id,
  label,
}));

export interface TextboxOptionsV2 {
  validationType?: ValidationTypeV2;
  multiline?: boolean;
  /** Initial visible rows of the autosizing multiline textarea; defaults to 4. */
  minRows?: number;
}

export interface IFormFieldV2 {
  invariantId?: string;
  subFormFieldId?: number;
  subFormFieldInvariantId?: string;
  name: string;
  label: string;
  type: FormFieldTypeV2;
  required?: boolean;
  description?: string;
  /**
   * Long-form help shown behind an info icon beside the label rather than as
   * inline prose under it. Use for guidance that would otherwise outweigh the
   * input; keep `description` for the short line a user must read to answer.
   */
  labelTooltip?: string;
  /** Overrides the automatic `Enter {label}` placeholder on text inputs. */
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  size?: FieldSizeV2;
  featureFlag?: FeatureFlagEnum;

  builderOptions?: {
    id?: number;
    version?: number;
    slug?: string;
    subFormDefinitionId?: number;
    subFormType?: SubFormTypeEnum;
    order?: number;
    defaultValue?: string;
    shortLabel?: string;
    isComplianceEventField?: boolean;
    complianceEventTriggerValue?: string;
    complianceStandardIds?: ISelectOption[];
    isFailureTriggerField?: boolean;
    failureTriggerValue?: string;
    referenceImageKey?: string;
    referenceImageUrl?: string;

    selectOptions?: IFormFieldDropDownOptions[];
  };

  visibility?: {
    isDefaultHidden?: boolean;
    rules?: VisibilityRule[];
  };
  validation?: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    patternError?: string;
    customError?: string;
    /** Values to reject: numbers within min/max (e.g. a 0 distinct from a negative sentinel) or select option ids. */
    excludeValues?: (string | number)[];
  };
  options?: ISelectOption[];
  textboxOptions?: TextboxOptionsV2;
  numberOptions?: {
    format?: 'currency';
  };
  switchOptions?: {
    positiveText?: string;
    negativeText?: string;
    positiveColor?: string;
    negativeColor?: string;
    /** Visual control style — button-group (default) or a compact toggle switch. */
    control?: 'buttons' | 'toggle';
  };
  fileUploadOptions?: {
    allowMultiple?: boolean;
    allowedFileTypes?: string[];
    maxFileSize?: number;
    showFileSizeWarning?: boolean;
    simplifiedUploader?: boolean;
    compact?: boolean;
    isHideUploadSuccessEnabled?: boolean;
    renderUploadsAsImage?: boolean;
  };
  assetOptions?: {
    rootAssetId?: number;
    includeLeaves?: boolean;
    requireLeaf?: boolean;
    showFullPath?: boolean;
  };
  personOptions?: {
    hideSelectAll?: boolean;
    includeInactive?: boolean;
    /** Name of an project field in the form to link personnel filtering to */
    projectFieldName?: string;
    /**
     * Opt-in extra audience dimensions beyond the default
     * Employee/Manager/Department/Group tabs (e.g. Vendor, Project,
     * Attribute). Existing consumers that don't set this are unaffected.
     */
    additionalEntityTypes?: EntityTypeEnum[];
  };
  subFormOptions?: {
    subFormDefinitionId?: number;
    showPreviewComponent?: boolean;
  };
  scaleOptions?: {
    leastValueText?: string;
    mostValueText?: string;
  };
  /**
   * Value shape for a `measurement` field is `{magnitude: number | '', unit:
   * string}` — an object, matching the object-valued convention used by
   * `asset`/`person`/`project`. `units` here mirrors the backend's sibling
   * `measurementUnits: string[]` (kept flat there for the FormField DTOs);
   * when exactly one unit is configured the renderer shows a fixed suffix
   * instead of a unit picker.
   */
  measurementOptions?: {
    minValue?: number;
    maxValue?: number;
    decimalPlaces?: number;
    units?: string[];
  };
}

export type IFormDataV2 = {
  [key: string]: any;
};

export const identifierFieldName: string = '__identifier';

export const identifierFormFieldV2: IFormFieldV2 = {
  name: identifierFieldName,
  label: 'Form Description',
  type: 'text',
  required: true,
  builderOptions: {
    order: -1,
  },
  validation: {
    maxLength: 100,
  },
  description:
    'Enter a short, one-sentence description (100 characters maxmimum) of your submission. This is used to help identify your submission at a later date.',
};
